import { Typography, Row, Col } from 'antd';
import React, { FC, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import features from '~/constants/features';
import { useGetPayload, useProceedStep, useSelectStep } from '~/controllers/wizard';
import BottomLeftActions from '~/layouts/BottomLeftActions';
import LeftLayout from '~/layouts/LeftLayout';
import WizardContent from '~/layouts/WizardContent';
import WizardContinueButton from '~/layouts/WizardContinueButton';
import { isAgent } from '~/utils/getDecodedJwt';
import paths from '~/pages/paths';
import { Stage } from '~/types/Application';
import { SalesforceSoftDeclineImg } from '~/img';
import dayjs from 'dayjs';
import { KsaReviewResult } from '~/types/ksa/KsaReviewResult';
import Section from '~/layouts/Section ';
import { proceedReviewResubmit } from '~/api/review';
import CompletionContainer from "~/pages/ksa/completion/components/CompletionContainer";

const ReviewSuccess: FC<void> = () => {
  const { t } = useTranslation('ksaCompletion');

  const history = useHistory();

  const selectStepMutation = useSelectStep();
  const payload = useGetPayload();
  const proceed = useProceedStep(proceedReviewResubmit);

  useEffect(() => {
    if (isAgent()) {
      void selectStepMutation
        .mutateAsync({
          stageKey: Stage.ksaReview,
          stepKey: 'review.completeReview',
        })
        .then(() => {
          history.push(`${paths.ksa.review}/completeReview`);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reviewHistory = (payload?.completeReview ?? [])
    .filter(({ decision }) => decision === 'RESUBMIT')
    ?.sort(({ date: date1 }, { date: date2 }) => dayjs(date2).diff(dayjs(date1)));

  const onClick = () => {
    proceed.mutateAsync().catch((e) => {
      console.error(`failed to submit; error:`, e);
    });
  };

  return isAgent() ? (
    <div />
  ) : (
    <WizardContent
      left={
        <LeftLayout>
          <BottomLeftActions contactUsUrl={features.contactUsLinkHardDecline} />
        </LeftLayout>
      }
      content={
        <CompletionContainer>
          <Typography.Title>{t('We need more information')}</Typography.Title>
          <Typography.Paragraph className="Completion-Paragraph">
            <p>
              {t(
                'Thank you for your application. We have reviewed the information submitted to us and require some clarifications.'
              )}
            </p>
            <p>
              {t(
                "We request you to select 'Update Application' below and review the following details which you provided:"
              )}
            </p>
            <p>{reviewHistory?.[0]?.reviewComment}</p>
            <p>
              <Trans
                t={t}
                i18nKey="Please contact us via our <1>Contact us</1> page if you need any support or have any questions."
              >
                {'Please contact us via our '}
                <a href={features.contactUsLink} target="__blank">
                  Contact us
                </a>{' '}
                page if you need any support or have any questions.
              </Trans>
            </p>
          </Typography.Paragraph>
          {reviewHistory.length > 1 ? <HistoryReview reviews={reviewHistory.slice(1)} /> : null}
          <SalesforceSoftDeclineImg className="Completion-Img" />
        </CompletionContainer>
      }
      right={
        <WizardContinueButton
          id="payment_continueButton"
          onClick={onClick}
          loading={proceed.isLoading}
        >
          {t('Update application')}
        </WizardContinueButton>
      }
    />
  );
};

interface HistoryReviewProps {
  reviews: KsaReviewResult[];
}
const HistoryReview: FC<HistoryReviewProps> = (props) => {
  const { reviews } = props;
  const { t } = useTranslation('ksaCompletion');
  return (
    <Section>
      <Typography.Title level={5}>
        {t('Application amendment history').toUpperCase()}
      </Typography.Title>
      <Row gutter={16}>
        <Col span={8}>
          <Typography.Paragraph strong>{t('Date')}</Typography.Paragraph>
        </Col>
        <Col span={16}>
          <Typography.Paragraph strong>{t('Reason')}</Typography.Paragraph>
        </Col>
      </Row>
      {reviews.map((review) => {
        return (
          <Row gutter={16} key={review.uid}>
            <Col span={8}>{dayjs(review.date).format('DD/MM/YYYY')}</Col>
            <Col span={16}>{review.reviewComment}</Col>
          </Row>
        );
      })}
    </Section>
  );
};


export default ReviewSuccess;
